import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { countryCodeOptionsForValue } from '@/constants/countryCodes';
import { handleApiError } from '../lib/errors';
import { type Vendor, type VendorInput, vendorService } from '../services/vendorService';
import { logger } from '../utils/logger';

type FormData = VendorInput;

const VENDOR_TYPE_OPTIONS = [
  { value: 'External', label: 'External' },
  { value: 'Intercompany', label: 'Intercompany' },
] as const;

const emptyForm: FormData = {
  vendor_id: '',
  vendor_name: '',
  vendor_type: '',
  country: '',
  postal_code: '',
  city: '',
  street: '',
  street_no: '',
};

function rowToForm(row: Vendor): FormData {
  return {
    vendor_id: row.vendor_id ?? '',
    vendor_name: row.vendor_name ?? '',
    vendor_type: row.vendor_type ?? '',
    country: row.country ?? '',
    postal_code: row.postal_code ?? '',
    city: row.city ?? '',
    street: row.street ?? '',
    street_no: row.street_no ?? '',
  };
}

function formToPayload(form: FormData): VendorInput {
  const trim = (v: string) => v.trim();
  return {
    vendor_id: trim(form.vendor_id ?? '') || undefined,
    vendor_name: trim(form.vendor_name ?? '') || undefined,
    vendor_type: trim(form.vendor_type ?? '') || undefined,
    country: trim(form.country ?? '') || undefined,
    postal_code: trim(form.postal_code ?? '') || undefined,
    city: trim(form.city ?? '') || undefined,
    street: trim(form.street ?? '') || undefined,
    street_no: trim(form.street_no ?? '') || undefined,
  };
}

interface VendorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: Vendor) => void;
  onEntrySaved?: () => void;
  row?: Vendor | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const VendorDrawer: React.FC<VendorDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  row,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && row) {
        setFormData(rowToForm(row));
      } else {
        setFormData(emptyForm);
      }
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, row]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = formToPayload(formData);
      const response =
        isEdit && row
          ? await vendorService.update(row.id, payload)
          : await vendorService.create(payload);
      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Error saving vendor:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;
  const countryOptions = useMemo(
    () => countryCodeOptionsForValue(formData.country),
    [formData.country]
  );

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Vendor' : isEdit ? 'Edit Vendor' : 'Add Vendor'}
      description={
        readOnly
          ? 'Vendor details.'
          : isEdit
            ? 'Update the vendor below.'
            : 'Fill in the vendor details.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="ID"
          value={formData.vendor_id ?? ''}
          onChange={(v) => handleChange('vendor_id', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Vendor Name"
          value={formData.vendor_name ?? ''}
          onChange={(v) => handleChange('vendor_name', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <SelectFormField
          label="Vendor Type"
          value={formData.vendor_type ?? ''}
          onChange={(v) => handleChange('vendor_type', v)}
          options={[...VENDOR_TYPE_OPTIONS]}
          placeholder="Select vendor type"
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <p className="pt-2 text-sm font-medium text-text-secondary">Address</p>
        <SelectFormField
          label="Country"
          value={formData.country ?? ''}
          onChange={(v) => handleChange('country', v)}
          options={countryOptions}
          placeholder="Select country"
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Postal Code"
          value={formData.postal_code ?? ''}
          onChange={(v) => handleChange('postal_code', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="City"
          value={formData.city ?? ''}
          onChange={(v) => handleChange('city', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Street"
          value={formData.street ?? ''}
          onChange={(v) => handleChange('street', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Street No"
          value={formData.street_no ?? ''}
          onChange={(v) => handleChange('street_no', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
